import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Clock, Users, Settings, MessageSquare, FileText } from 'lucide-react';
// [ADM STORAGE GRAFICO] Import storage chart component
import StorageChart from '../components/StorageChart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [sessoesEspera, setSessoesEspera] = useState([]);
  const [naoLidasCount, setNaoLidasCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // Poll for unread messages every 10 seconds
    const interval = setInterval(fetchNaoLidas, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [usuariosRes, sessoesRes] = await Promise.all([
        axios.get(`${API}/admin/usuarios`),
        axios.get(`${API}/admin/sessoes/espera`)
      ]);
      // Handle both response formats: {items: [...], total: X} or [...]
      const usuariosData = usuariosRes.data?.items || usuariosRes.data || [];
      setUsuarios(usuariosData);
      setSessoesEspera(sessoesRes.data);
      await fetchNaoLidas();
    } catch (error) {
      // Silent error - don't show toast on dashboard load
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNaoLidas = async () => {
    try {
      const response = await axios.get(`${API}/mensagens-suporte/nao-lidas/count`);
      setNaoLidasCount(response.data.count);
    } catch (error) {
      console.error('Erro ao buscar contagem de não lidas:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#191919]">
        <div className="text-white text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#191919] text-white">
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Painel Administrativo</h1>
            <p className="text-gray-400 text-sm mt-1">Bem-vindo, {user?.nome}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-[#CF0D1D] text-[#CF0D1D] hover:bg-[#CF0D1D] hover:text-white"
            data-testid="admin-logout-button"
          >
            Sair
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Gerenciamento do Sistema</h2>
          <p className="text-gray-400">Selecione uma opção para gerenciar</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Card Gerenciar Usuários */}
          <Card
            className="bg-white border-gray-200 cursor-pointer hover:scale-105 transition-transform hover:shadow-xl"
            onClick={() => navigate('/admin/usuarios')}
            data-testid="gerenciar-usuarios-card"
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-600/10 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-[#191919] text-xl">Gerenciar Usuários</CardTitle>
                  <CardDescription className="text-gray-600">
                    {usuarios.length} usuários cadastrados
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-sm">
                Aprovar, editar, bloquear ou excluir usuários do sistema
              </p>
            </CardContent>
          </Card>

          {/* Card Gerenciar Estações */}
          <Card
            className="bg-white border-gray-200 cursor-pointer hover:scale-105 transition-transform hover:shadow-xl"
            onClick={() => navigate('/admin/estacoes')}
            data-testid="gerenciar-estacoes-card"
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-600/10 rounded-full flex items-center justify-center">
                  <Settings className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-[#191919] text-xl">Gerenciar Estações</CardTitle>
                  <CardDescription className="text-gray-600">Upload de roteiros PDF</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-sm">
                Gerenciar roteiros em PDF para médicos e atores
              </p>
            </CardContent>
          </Card>

          {/* Card Suporte */}
          <Card
            className="bg-white border-gray-200 cursor-pointer hover:scale-105 transition-transform hover:shadow-xl"
            onClick={() => navigate('/admin/suporte')}
            data-testid="suporte-card"
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-600/10 rounded-full flex items-center justify-center relative">
                  <MessageSquare className="w-6 h-6 text-green-600" />
                  {naoLidasCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[#CF0D1D] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {naoLidasCount}
                    </span>
                  )}
                </div>
                <div>
                  <CardTitle className="text-[#191919] text-xl">
                    Mensagens de Suporte
                    {naoLidasCount > 0 && (
                      <span className="ml-2 text-sm text-[#CF0D1D]">
                        ({naoLidasCount} nova{naoLidasCount > 1 ? 's' : ''})
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription className="text-gray-600">Ver mensagens dos médicos</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-sm">
                Responder e gerenciar mensagens de suporte
              </p>
            </CardContent>
          </Card>

          {/* Card Gestão de Resumos */}
          <Card
            className="bg-white border-gray-200 cursor-pointer hover:scale-105 transition-transform hover:shadow-xl"
            onClick={() => navigate('/admin/resumos')}
            data-testid="gestao-resumos-card"
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-600/10 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-[#191919] text-xl">
                    Gestão de Resumos
                  </CardTitle>
                  <CardDescription className="text-gray-600">Gerenciar PDFs de resumos</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 text-sm">
                Upload e gerenciamento de PDFs de resumos por especialidade
              </p>
            </CardContent>
          </Card>
        </div>

        {/* [ADM STORAGE GRAFICO] Storage Monitoring Section */}
        <div className="mt-8">
          <h3 className="text-2xl font-bold mb-4 text-white">Monitoramento de Storage</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <StorageChart />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
